import { useConfig } from "@/context/ConfigContext";
import {
  getAuthor,
  getCanonicalUrl,
  getConfigValue,
  getDefaultDescription,
  getDefaultKeywords,
  getDefaultTitle,
  getFavicon,
  getOgImage,
  getRobots,
  getSiteName,
  getSocialLinks,
  getTwitterHandle,
} from "@/utils/configHelper";
import Head from "next/head";

export const MetaHead = ({
  title,
  description,
  keywords,
  ogImage,
  ogUrl,
  favicon,
  robots,
  language,
  twitterTitle,
  twitterDescription,
  twitterImage,
  googleSiteVerification,
  bingSiteVerification,
  yandexSiteVerification,
  pagePath = "",
}) => {
  const { config } = useConfig();

  const toSafeString = (value, fallback = "") => {
    if (value === null || value === undefined) return fallback;
    if (typeof value === "string") return value;
    if (typeof value === "number" || typeof value === "boolean") {
      return String(value);
    }
    return fallback;
  };

  const ensureAbsoluteUrl = (value, fallbackBase = "") => {
    const normalizedValue = toSafeString(value, "").trim();
    if (!normalizedValue) return normalizedValue;
    if (/^https?:\/\//i.test(normalizedValue)) return normalizedValue;
    const base = (fallbackBase || "").trim();
    if (!base) return normalizedValue;
    const normalizedBase = /^https?:\/\//i.test(base)
      ? base
      : `https://${base}`;
    const normalizedPath = normalizedValue.startsWith("/")
      ? normalizedValue
      : `/${normalizedValue}`;
    try {
      return new URL(normalizedPath, normalizedBase).toString();
    } catch {
      return `${normalizedBase.replace(/\/+$/, "")}/${normalizedPath.replace(
        /^\/+/,
        "",
      )}`;
    }
  };

  // Get values from config with fallback to defaults
  const siteName = getSiteName(config);
  const defaultTitle = getDefaultTitle(config);
  const defaultDescription = getDefaultDescription(config);
  const defaultKeywords = getDefaultKeywords(config);
  const author = getAuthor(config);
  const defaultRobots = getRobots(config);
  const defaultOgImage = getOgImage(config);
  const baseUrl = getConfigValue(config, "site.baseUrl", "");
  const faviconHref = ensureAbsoluteUrl(
    toSafeString(favicon, "") || getFavicon(config),
    baseUrl,
  );
  const finalRobots = toSafeString(robots, "") || defaultRobots;
  const finalLanguage = toSafeString(language, "");

  // Use provided values or fallback to config defaults (never empty string untuk crawler)
  const DEFAULT_TITLE = "BaseStore";
  const DEFAULT_DESCRIPTION = "Online store untuk berbagai kebutuhan Anda";
  const finalTitle =
    toSafeString(title, "") || toSafeString(defaultTitle, "") || DEFAULT_TITLE;
  const finalDescription =
    toSafeString(description, "") ||
    toSafeString(defaultDescription, "") ||
    DEFAULT_DESCRIPTION;
  const finalKeywords =
    toSafeString(keywords, "") ||
    toSafeString(defaultKeywords, "") ||
    "toko online, belanja online, ecommerce";
  const finalOgImage = ensureAbsoluteUrl(
    toSafeString(ogImage, "") ||
      toSafeString(defaultOgImage, "") ||
      "/images/og-image.jpg",
    baseUrl,
  );
  const canonicalFromConfig = getCanonicalUrl(config, pagePath);
  const rawCanonical =
    ogUrl ||
    canonicalFromConfig ||
    (pagePath ? (pagePath.startsWith("/") ? pagePath : `/${pagePath}`) : "/");
  const finalOgUrl = ensureAbsoluteUrl(
    toSafeString(rawCanonical, "") || "/",
    baseUrl,
  );
  const twitterHandle = getTwitterHandle(config);
  const socialLinks = getSocialLinks(config);

  // Use provided Twitter values or fallback to main values
  const finalTwitterTitle = twitterTitle || finalTitle;
  const finalTwitterDescription = twitterDescription || finalDescription;
  const finalTwitterImage = twitterImage || finalOgImage;

  return (
    <Head>
      <meta charSet="UTF-8" />
      <meta name="viewport" content="width=device-width, initial-scale=1" />
      <meta name="description" content={finalDescription} />
      <meta name="keywords" content={finalKeywords} />
      <meta name="author" content={author} />
      <meta name="robots" content={finalRobots} />
      {googleSiteVerification && (
        <meta
          name="google-site-verification"
          content={googleSiteVerification}
        />
      )}
      {bingSiteVerification && (
        <meta name="msvalidate.01" content={bingSiteVerification} />
      )}
      {yandexSiteVerification && (
        <meta
          name="yandex-verification"
          content={yandexSiteVerification}
        />
      )}
      {finalLanguage && (
        <meta httpEquiv="content-language" content={finalLanguage} />
      )}
      <link rel="canonical" href={finalOgUrl} />

      {/* Open Graph tags */}
      <meta property="og:title" content={finalTitle} />
      <meta property="og:description" content={finalDescription} />
      <meta property="og:image" content={finalOgImage} />
      <meta property="og:type" content="website" />
      <meta property="og:url" content={finalOgUrl} />
      <meta property="og:site_name" content={siteName} />

      {/* Distribution, subject and related meta tags */}
      <meta name="distribution" content="global" />
      <meta name="subject" content={finalKeywords} />
      <meta name="revisit-after" content="7 days" />
      <meta httpEquiv="X-UA-Compatible" content="IE=edge" />
      <meta name="copyright" content={author} />
      <meta name="rating" content="general" />

      {/* Twitter cards */}
      <meta name="twitter:title" content={finalTwitterTitle} />
      <meta name="twitter:description" content={finalTwitterDescription} />
      <meta name="twitter:image" content={finalTwitterImage} />
      <meta name="twitter:card" content="summary_large_image" />
      {twitterHandle && (
        <>
          <meta name="twitter:site" content={twitterHandle} />
          <meta name="twitter:creator" content={twitterHandle} />
        </>
      )}

      {/* Application meta */}
      <meta name="application-name" content={siteName} />
      <meta name="apple-mobile-web-app-title" content={siteName} />
      <link
        rel="preconnect"
        href="https://o4504710970146816.ingest.us.sentry.io"
      ></link>
      <link rel="preconnect" href="https://console-spaces.bullnium.com"></link>

      {/* Schema.org Organization */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "http://schema.org",
          "@type": "Organization",
          name: siteName,
          url: baseUrl,
          description: finalDescription,
          logo: finalOgImage,
          sameAs: socialLinks.length > 0 ? socialLinks : undefined,
        })}
      </script>

      {/* Schema.org Service */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "http://schema.org",
          "@type": "Service",
          serviceType: finalKeywords,
          provider: {
            "@type": "Organization",
            name: siteName,
          },
          description: finalDescription,
          areaServed: "Indonesia",
        })}
      </script>

      {/* Schema.org LocalBusiness */}
      {baseUrl && (
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "http://schema.org",
            "@type": "LocalBusiness",
            name: siteName,
            image: finalOgImage,
            "@id": baseUrl,
            url: baseUrl,
            openingHoursSpecification: [
              {
                "@type": "OpeningHoursSpecification",
                dayOfWeek: [
                  "Monday",
                  "Tuesday",
                  "Wednesday",
                  "Thursday",
                  "Friday",
                  "Saturday",
                  "Sunday",
                ],
                opens: "00:00",
                closes: "23:59",
              },
            ],
            sameAs: socialLinks.length > 0 ? socialLinks : undefined,
          })}
        </script>
      )}

      {/* FAQPage Schema - Optional, bisa di-disable jika tidak diperlukan */}
      {getConfigValue(config, "seo.enableFAQSchema", false) && (
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: getConfigValue(config, "seo.faqSchema", []),
          })}
        </script>
      )}

      {/* Favicons and app icons */}
      <link rel="apple-touch-icon" sizes="57x57" href="/apple-icon-57x57.png" />
      <link rel="apple-touch-icon" sizes="60x60" href="/apple-icon-60x60.png" />
      <link rel="apple-touch-icon" sizes="72x72" href="/apple-icon-72x72.png" />
      <link rel="apple-touch-icon" sizes="76x76" href="/apple-icon-76x76.png" />
      <link
        rel="apple-touch-icon"
        sizes="114x114"
        href="/apple-icon-114x114.png"
      />
      <link
        rel="apple-touch-icon"
        sizes="120x120"
        href="/apple-icon-120x120.png"
      />
      <link
        rel="apple-touch-icon"
        sizes="144x144"
        href="/apple-icon-144x144.png"
      />
      <link
        rel="apple-touch-icon"
        sizes="152x152"
        href="/apple-icon-152x152.png"
      />
      <link
        rel="apple-touch-icon"
        sizes="180x180"
        href="/apple-icon-180x180.png"
      />
      <link rel="icon" type="image/webp" sizes="192x192" href={faviconHref} />
      <link rel="icon" type="image/png" sizes="32x32" href={faviconHref} />
      <link rel="icon" type="image/png" sizes="96x96" href={faviconHref} />
      <link rel="icon" type="image/png" sizes="16x16" href={faviconHref} />
      <link rel="manifest" href="/manifest.json" />
      <meta name="msapplication-TileColor" content="#ffffff" />
      <meta name="msapplication-TileImage" content="/ms-icon-144x144.png" />
      <meta name="theme-color" content="#ffffff" />
      <link rel="icon" href={faviconHref} />
      <title>{finalTitle}</title>
    </Head>
  );
};
